import { Geolocation } from "@capacitor/geolocation";
import { IonIcon } from "@ionic/react";
import { refreshOutline } from "ionicons/icons";
import { debounce } from 'lodash';
import mapboxgl from 'mapbox-gl'; // or "const mapboxgl = require('mapbox-gl');"
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useHistory } from "react-router";
import { AppContext } from "../../../AppStateProvider";
import MapPopup from "../MapPopup/MapPopup";
import './WorldWeb.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

interface ContainerProps {
    lat: number;
    long: number;
    zoom: number;
    geojson: any;
    onClick?: any;
    blurred: boolean;
    flyTo: boolean;
    flyToCoords?: number[];
    flyToZoom?: number;
    isNative?: boolean;
    isSubscribed?: boolean;
    onBoundsChange?: (bounds: {
        ne_lat: number;
        ne_long: number;
        sw_lat: number;
        sw_long: number;
    }) => void;
    onUserLocationUpdate?: (coords: {
        latitude: number;
        longitude: number;
    }) => void;
}

const DEBOUNCE_DELAY = 300;

const WorldWebOriginal: React.FC<ContainerProps> = (props) => {
    const { state } = useContext(AppContext);
    const history = useHistory();
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [long, setLong] = useState(props.long); // between -180 and 180
    const [lat, setLat] = useState(props.lat); // between -90 and 90
    const [zoom, setZoom] = useState(props.zoom); // Zoom levels: https://docs.mapbox.com/help/glossary/zoom-level/
    const [is3D, setIs3D] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [locationSearch, setLocationSearch] = useState(undefined);
    const [showShop, setShowShop] = useState(false);
    const loadedImages = useRef<Set<string>>(new Set());

    const previousGeojson = useRef(null);
    const watchIdRef = useRef<string | number | null>(null);
    const activePopup = useRef<mapboxgl.Popup | null>(null);
    const userMarkerRef = useRef<mapboxgl.Marker | null>(null);

    // Add debounced bounds handler
    const handleBoundsChange = useCallback(
        debounce(() => {
            if (map.current && props.onBoundsChange) {
                const bounds = map.current.getBounds();
                props.onBoundsChange({
                    ne_lat: bounds.getNorthEast().lat,
                    ne_long: bounds.getNorthEast().lng,
                    sw_lat: bounds.getSouthWest().lat,
                    sw_long: bounds.getSouthWest().lng
                });
            }
        }, DEBOUNCE_DELAY),
        [props.onBoundsChange]
    );

    const updateUserLocation = (coords: { longitude: number, latitude: number }) => {
        if (!map.current || !userMarkerRef.current) return;

        // Only update marker position, don't center the map
        userMarkerRef.current.setLngLat([coords.longitude, coords.latitude]);

        // Notify parent component of location update
        if (props.onUserLocationUpdate) {
            props.onUserLocationUpdate({
                latitude: coords.latitude,
                longitude: coords.longitude
            });
        }
    };

    const checkGeoLocationPermission = async () => {
        const permissions = await Geolocation.checkPermissions();
        if (permissions.location === 'denied' || permissions.location === 'granted') {
            return true;
        } else {
            return false;
        }
    }

    const getGeoLocationMobile = async () => {
        const checkLocationRes = await checkGeoLocationPermission();
        if (!checkLocationRes) {
            const res = await Geolocation.requestPermissions();
            if (res.location === 'granted') {
                return true
            } else {
                return false;
            }
        } else {
            const permissions = await Geolocation.checkPermissions();
            if (permissions.location === 'denied') {
                return false;
            }
            if (permissions.location === 'granted') {
                return true;
            }
        }
        return false;
    }

    const initializeNativeLocation = async () => {
        try {
            const accessToLocation = await getGeoLocationMobile();
            if (accessToLocation) {
                const coordinates = await Geolocation.getCurrentPosition();

                // Check if map is properly initialized before adding markers
                if (!map.current || !map.current.isStyleLoaded()) {
                    console.warn('Map not ready for marker placement');
                    return;
                }

                // Create user marker only once
                const uel = document.createElement('div');
                uel.classList.add('map-usermarker__border');
                uel.innerHTML = '<div class="map-usermarker__core"></div>';

                userMarkerRef.current = new mapboxgl.Marker({
                    element: uel,
                })
                    .setLngLat([coordinates.coords.longitude, coordinates.coords.latitude])
                    .addTo(map.current);

                // Notify parent component of initial location
                if (props.onUserLocationUpdate) {
                    props.onUserLocationUpdate({
                        latitude: coordinates.coords.latitude,
                        longitude: coordinates.coords.longitude
                    });
                }

                // Set initial position
                if (!props.flyTo) {
                    map.current.jumpTo({
                        center: [coordinates.coords.longitude, coordinates.coords.latitude],
                        essential: true,
                        zoom: 9
                    });
                }

                // Store the watch ID for cleanup
                const watchId = await Geolocation.watchPosition({
                    enableHighAccuracy: false,
                    timeout: 30000,
                    maximumAge: 10000,
                }, (position, err) => {
                    if (position) {
                        updateUserLocation(position.coords);
                    }
                });

                watchIdRef.current = watchId;
            }
        } catch (e) {
            console.error('Error initializing native location:', e);
        }
    };

    const initializeBrowserLocation = async () => {
        try {
            navigator.geolocation.getCurrentPosition(
                async function (position) {
                    // Check if map is properly initialized before adding markers
                    if (!map.current || !map.current.isStyleLoaded()) {
                        console.warn('Map not ready for marker placement');
                        return;
                    }

                    const uel = document.createElement('div');
                    uel.classList.add('map-usermarker__border');
                    uel.innerHTML = '<div class="map-usermarker__core"></div>';

                    userMarkerRef.current = new mapboxgl.Marker({
                        element: uel,
                    })
                        .setLngLat([position.coords.longitude, position.coords.latitude])
                        .addTo(map.current);

                    // Notify parent component of initial location
                    if (props.onUserLocationUpdate) {
                        props.onUserLocationUpdate({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        });
                    }

                    // Set initial position only
                    if (!props.flyTo) {
                        console.log("flying to!");
                        map.current.jumpTo({
                            center: [position.coords.longitude, position.coords.latitude],
                            essential: true,
                            zoom: 9
                        });
                    } else {
                        console.log("Not flying to!");
                    }

                    // Watch position without auto-centering
                    // Store the watch ID for cleanup
                    const watchId = navigator.geolocation.watchPosition(
                        (newPosition) => {
                            updateUserLocation(newPosition.coords);
                        },
                        (error) => {
                            console.error("Error watching position:", error);
                        },
                        {
                            enableHighAccuracy: false,
                            timeout: 30000,
                            maximumAge: 10000
                        }
                    );

                    watchIdRef.current = watchId;
                },
                function (error) {
                    console.error("Error getting position:", error);
                }
            );
        } catch (e) {
            console.error('Error initializing browser location:', e);
        }
    };


    useEffect(() => {
        if (map.current) return;

        map.current = new mapboxgl.Map({
            container: mapContainer.current!,
            style: 'mapbox://styles/brendanhart/clo8icc5500bd01rfcl0b6acx',
            center: [long, lat],
            zoom: zoom,
            maxZoom: 18,
            preserveDrawingBuffer: false,
            trackResize: true,
            renderWorldCopies: false // Prevents map from repeating across the globe
        });

        // Add navigation control with minimized pitch control for better performance
        map.current.addControl(
            new mapboxgl.NavigationControl({ visualizePitch: false }),
            "bottom-right"
        );

        // Configure geolocation control with optimized settings
        map.current.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: false, // Reduces battery usage
                    timeout: 6000
                },
                trackUserLocation: true,
                showUserHeading: false // Disable heading indicator for better performance
            }),
            "bottom-right"
        );

        // Initialize map with optimized image loading
        map.current.on('load', async () => {
            initializeMap();
            // Clean up unused resources after initial load
            handleBoundsChange();
            // Try to update map source after initial load if geojson is available
            if (props.geojson) {
                setTimeout(() => {
                    updateMapSource();
                }, 100);
            }

            // Initialize location services after map is fully loaded
            if (props.isNative) {
                await initializeNativeLocation();
            } else {
                await initializeBrowserLocation();
            }
        });


        // Add bounds change listeners
        map.current.on('moveend', handleBoundsChange);
        map.current.on('zoomend', handleBoundsChange);

        return () => {
            // Cleanup geolocation watching
            if (watchIdRef.current !== null) {
                if (props.isNative) {
                    Geolocation.clearWatch({ id: watchIdRef.current as string });
                } else {
                    navigator.geolocation.clearWatch(watchIdRef.current as number);
                }
                watchIdRef.current = null;
            }

            activePopup.current?.remove();

            // Add cleanup for 'load' event
            map.current?.off('load');
            handleBoundsChange.cancel();
            map.current?.off('moveend', handleBoundsChange);
            map.current?.off('zoomend', handleBoundsChange);
            // Clean up the move listener added in another useEffect
            map.current?.off('move');
            // Clean up cluster and point click listeners
            map.current?.off('click', 'clusters');
            map.current?.off('click', 'unclustered-point');
            map.current?.off('mouseenter', 'clusters');
            map.current?.off('mouseleave', 'clusters');
            // Clean up style image missing handler
            map.current?.off('styleimagemissing');

            // Remove user marker if it exists
            userMarkerRef.current?.remove();

            map.current?.remove();

            // Clear the loaded images set
            loadedImages.current.clear();
        };
    }, []);

    // Add this new useEffect hook
    useEffect(() => {
        if (map.current) {
            const resizeMap = () => {
                map.current.resize();
            };

            // Resize immediately
            resizeMap();

            // Resize after a short delay
            const timeoutId = setTimeout(resizeMap, 100);

            // Resize on window resize
            window.addEventListener('resize', resizeMap);

            // Clean up
            return () => {
                clearTimeout(timeoutId);
                window.removeEventListener('resize', resizeMap);
            };
        }
    }, [map.current]); // Only re-run if map.current changes

    const initializeMap = () => {
        if (!map.current) return;

        map.current.resize();

        // add the DEM source as a terrain layer with exaggerated height
        map.current.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1.5 });

        // ADD CLOWN SHIT
        map.current.loadImage(
            'https://corked.blob.core.windows.net/logos/clown.png',
            (error: any, image: any) => {
                if (error) throw error;

                // Add the image to the map style.
                map.current.addImage('clown', image);

                // Add a data source containing one point feature.
                map.current.addSource('clown-towns', {
                    'type': 'geojson',
                    'data': {
                        'type': 'FeatureCollection',
                        'features': [
                            {
                                'type': 'Feature',
                                'geometry': {
                                    'type': 'Point',
                                    'coordinates': [7.672850, 46.729440]
                                }
                            }
                        ]
                    }
                });

                // Add a layer to use the image to represent the data.
                map.current.addLayer({
                    'id': 'clowning',
                    'type': 'symbol',
                    'source': 'clown-towns', // reference the data source
                    'layout': {
                        'icon-image': 'clown', // reference the image
                        "icon-size": [
                            'interpolate',
                            // Set the exponential rate of change to 1.5
                            ['linear'],
                            ['zoom'],
                            // When zoom is 10, icon will be 50% size.
                            10,
                            0.01,
                            // When zoom is 22, icon will be 10% size.
                            22,
                            2
                        ], // Use the 'iconSize' property to specify the icon size for each feature
                    }
                });
            }
        );

        if (!props.isNative) {
            map.current.addSource('idx-nation', {
                'type': 'geojson',
                'data': {
                    'type': 'Feature',
                    'properties': {},
                    'geometry': {
                        'type': 'Polygon',
                        'coordinates': [
                            [
                                [-180, -90],
                                [180, -90],
                                [180, -60],
                                [-180, -60],
                                [-180, -90]
                            ]
                        ]
                    }
                }
            });

            map.current.loadImage(
                'https://corked.blob.core.windows.net/logos/ID_logowithname-04.png',
                (err: any, image: any) => {
                    // Throw an error if something goes wrong.
                    if (err) throw err;

                    // Add the image to the map style.
                    map.current.addImage('idx-logo', image);

                    // Create a new layer and style it using `fill-pattern`.
                    map.current.addLayer({
                        'id': 'idx-layer',
                        'type': 'fill',
                        'source': 'idx-nation',
                        'paint': {
                            'fill-pattern': 'idx-logo'
                        }
                    });
                }
            );
        }
    };

    // Add this helper function at the top
    const areGeoJsonFeaturesEqual = (features1: any[], features2: any[]) => {
        if (features1.length !== features2.length) return false;

        return features1.every((feature1, index) => {
            const feature2 = features2[index];
            return (
                feature1.geometry.coordinates[0] === feature2.geometry.coordinates[0] &&
                feature1.geometry.coordinates[1] === feature2.geometry.coordinates[1] &&
                feature1.properties.id === feature2.properties.id
            );
        });
    };

    const updateMapSource = () => {
        if (!map.current || !props.geojson || !props.geojson.features || props.geojson.features.length === 0) return;

        // Check if the map style is loaded before attempting to add/update sources
        if (!map.current.isStyleLoaded()) {
            console.log("Style not loaded yet, waiting...");
            return;
        }

        // Check if the data has actually changed
        if (previousGeojson.current &&
            areGeoJsonFeaturesEqual(previousGeojson.current.features, props.geojson.features)) {
            return; // Skip update if data hasn't changed
        }

        const sourceId = 'tricks';

        // Create a function to add all layers
        const addLayers = () => {
            // Add clusters layer
            map.current.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'tricks',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': [
                        'step',
                        ['get', 'point_count'],
                        '#51bbd6',
                        10,
                        '#f1f075',
                        30,
                        '#f28cb1',
                        50,
                        '#5861AC'
                    ],
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        15,
                        10,
                        25,
                        30,
                        35,
                        50,
                        45
                    ]
                }
            });

            map.current.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'tricks',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12
                }
            });

            map.current.addLayer({
                id: 'unclustered-point',
                type: 'symbol',
                source: 'tricks',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': ['get', 'image'],
                    "icon-size": [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        10,
                        0.05,
                        22,
                        0.3
                    ],
                    'icon-allow-overlap': true,
                    'icon-padding': 10,
                }
            });
        };

        if (map.current.getSource(sourceId)) {
            // Update existing source
            const source = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
            source.setData(props.geojson);
        } else {
            // Initial source setup
            map.current.addSource(sourceId, {
                type: 'geojson',
                data: props.geojson,
                cluster: true,
                clusterMaxZoom: 14,
                clusterRadius: 50,
                generateId: true,
                maxzoom: 18
            });

            // Load all images before adding layers
            const imageLoadPromises = props.geojson.features.map((feature: GeoJSON.Feature) => {
                const image = feature.properties?.image as string;
                if (image && !loadedImages.current.has(image)) {
                    return new Promise((resolve) => {
                        map.current!.loadImage(image, (error: any, loadedImage: any) => {
                            if (error) {
                                console.warn(`Failed to load image ${image}:`, error);
                                resolve(null);
                                return;
                            }
                            if (!map.current!.hasImage(image)) {
                                try {
                                    map.current!.addImage(image, loadedImage);
                                    loadedImages.current.add(image);
                                } catch (e) {
                                    console.warn('Error adding image:', e);
                                }
                            }
                            resolve(image);
                        });
                    });
                }
                return Promise.resolve(null);
            });

            // Handle missing images
            const handleMissingImage = (e: any) => {
                const id = e.id;
                map.current!.loadImage(id, (error: any, loadedImage: any) => {
                    if (error) return;
                    if (!map.current!.hasImage(id)) {
                        map.current!.addImage(id, loadedImage);
                        loadedImages.current.add(id);
                    }
                });
            };

            map.current.on('styleimagemissing', handleMissingImage);

            // Wait for images to load then add layers and event listeners
            Promise.all(imageLoadPromises)
                .then(() => {
                    addLayers();

                    // Add event listeners
                    map.current.on('click', 'clusters', (e: any) => {
                        const features = map.current.queryRenderedFeatures(e.point, {
                            layers: ['clusters']
                        });
                        const clusterId = features[0].properties.cluster_id;
                        map.current.getSource('tricks').getClusterExpansionZoom(
                            clusterId,
                            (err: any, zoom: any) => {
                                if (err) return;
                                map.current.easeTo({
                                    center: features[0].geometry.coordinates,
                                    zoom: zoom
                                });
                            }
                        );
                    });

                    map.current!.on('click', 'unclustered-point', (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
                        if (!e.features || e.features.length === 0) return;

                        const coordinates = e.features[0].geometry.type === 'Point'
                            ? (e.features[0].geometry as GeoJSON.Point).coordinates.slice() as [number, number]
                            : e.lngLat.toArray();

                        const flyToClick = () => {
                            map.current!.jumpTo({
                                center: coordinates,
                                essential: true,
                            });
                        };

                        flyToClick();

                        const trick = JSON.parse(e.features[0].properties!.trick as string);

                        // Remove previous popup if it exists
                        activePopup.current?.remove();

                        const popupNode = document.createElement('div');
                        const root = createRoot(popupNode);
                        root.render(
                            <MapPopup
                                trick={trick}
                                onClick={onTrickClick}
                                onClickFlyTo={flyToClick}
                                loggedInId={state.user.id}
                            />
                        );

                        const trickPopup = new mapboxgl.Popup({
                            closeOnClick: true,
                            closeButton: false,
                        })
                            .setLngLat(coordinates)
                            .setDOMContent(popupNode)
                            .addTo(map.current!);

                        // Store reference to current popup
                        activePopup.current = trickPopup;

                        trickPopup.on('close', () => {
                            root.unmount();
                            activePopup.current = null;
                        });
                    });

                    map.current.on('mouseenter', 'clusters', () => {
                        map.current.getCanvas().style.cursor = 'pointer';
                    });
                    map.current.on('mouseleave', 'clusters', () => {
                        map.current.getCanvas().style.cursor = '';
                    });
                });
        }

        // Store the current geojson for future comparison
        previousGeojson.current = props.geojson;
    };

    useEffect(() => {
        if (!map.current || !props.geojson) return;

        // If style is not loaded, wait for it to load
        if (!map.current.isStyleLoaded()) {
            const handleStyleLoad = () => {
                updateMapSource();
                map.current?.off('style.load', handleStyleLoad);
            };
            map.current.on('style.load', handleStyleLoad);
            return;
        }

        updateMapSource();
    }, [props.geojson]);

    useEffect(() => {
        if (!map.current) return; // wait for map to initialize

        map.current.on('move', () => {
            setLong(map.current.getCenter().lng.toFixed(4));
            setLat(map.current.getCenter().lat.toFixed(4));
            setZoom(map.current.getZoom().toFixed(2));
        });
    }, []);

    useEffect(() => {
        setShowShop(!props.isSubscribed);
    }, [props.isSubscribed]);

    useEffect(() => {
        if (!map.current || !props.flyTo || !props.flyToCoords) return;

        map.current.resize();
        map.current.jumpTo({
            center: props.flyToCoords,
            essential: true,
            zoom: props.flyToZoom || 9  // Default to 9 if not specified
        });
    }, [props.flyTo, props.flyToCoords, props.flyToZoom]);

    const onTrickClick = (trickId: string, userId: string) => {
        map.current!.fire('closeAllPopups');
        props.onClick(trickId, userId);
    };

    const onToggle3D = () => {
        if (is3D) {
            setIs3D(false);
            map.current!.easeTo({ pitch: 0, bearing: 0 });
        } else {
            setIs3D(true);
            map.current!.easeTo({ pitch: 70, bearing: -30 });
        }
        map.current!.resize();
    };

    const onToggleSearch = () => {
        setShowSearch(!showSearch);
    };

    const resizeMap = () => {
        if (!map.current) return;
        map.current!.resize();
    };

    return (
        <div className="world-web-container" style={{ height: '100%', width: '100%' }}>
            {props.blurred ? <></> :
                <>
                    {/* Resize button - bottom left corner */}
                    <div className="fixed bottom-4 left-4 z-50 sm:bottom-6 sm:left-6 md:bottom-8 md:left-8">
                        <div
                            className="w-10 h-10 rounded-full flex justify-center items-center circle-button text-sm text-black shadow-lg"
                            onClick={resizeMap}>
                            <IonIcon slot="icon-only" icon={refreshOutline}></IonIcon>
                        </div>
                    </div>
                </>
            }
            <div ref={mapContainer} className={`map-container ${props.blurred ? "blurred_map" : ""}`} />
        </div>
    );
};

export default WorldWebOriginal;
